/**
 * Segment OAuth popup for the source connector (`/integrations/segment_in`).
 *
 * Authorizing returns an authorization code, which the caller posts to
 * `POST /v1/environments/{id}/oauth2/segment` together with the workspace's API
 * credentials. The backend exchanges the code for a Segment install token and uses
 * it to manage the Customer.io destination in the reader's Segment workspace.
 */
import { openOAuthPopup, type OAuthOpenResponse } from './oauth-popup';
import { getSegmentOAuthClientId } from './segment-client-id';

const SEGMENT_PROVIDER = 'segment';
const SEGMENT_AUTHORIZATION_URL = 'https://id.segmentapis.com/oauth2/auth';
const SEGMENT_SCOPE = 'destination/customer.io';
const SEGMENT_POPUP_WIDTH = 640;
const SEGMENT_CONSENT_TIMEOUT_MS = 90_000;

/**
 * The Segment OAuth app registers this path, not the `/oauth2/redirect` the other
 * providers use. Both are served by the same backend handler, so the difference is
 * only which URI Segment will accept.
 */
function getSegmentRedirectUri(): string {
  return `${window.location.origin}/torii/redirect.html`;
}

export function openSegmentOAuthPopup(): Promise<OAuthOpenResponse> {
  return openOAuthPopup({
    provider: SEGMENT_PROVIDER,
    baseUrl: SEGMENT_AUTHORIZATION_URL,
    clientId: getSegmentOAuthClientId(),
    scope: SEGMENT_SCOPE,
    redirectUri: getSegmentRedirectUri(),
    width: SEGMENT_POPUP_WIDTH,
    // Segment sends `id.segmentapis.com/oauth2/auth` on to `app.segment.com/authorize`
    // in a window we did not open, so the handle we hold closes while consent is
    // still running and cannot be used to end the wait.
    discardsPopupWindow: true,
    // Which means an abandoned popup holds the connector busy until this expires —
    // there is no signal that separates "walked away" from "still choosing a
    // source". Consent measured ~10s end to end, so this is generous for the flow
    // while keeping the worst case tolerable; the shared 3-minute default is meant
    // for interactive sign-in, not for this. A cancel control on the connector is
    // the real answer and is page-level work.
    timeoutMs: SEGMENT_CONSENT_TIMEOUT_MS,
  });
}
